import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import moment from 'moment';
import { getToken } from '../auth0';
import Logger from '../common/logger';
import { config } from '../config';
import {
    processAssetData,
    processAssetGroupData,
    processAssetGroupsData,
    processBookingData,
    processDepotData,
    processIssueReportData,
    processLocationBookingStats,
    processLocationData,
    processMultipleBookingsData,
    processThingsData,
} from './processor';
import {
    ApartmentAssets,
    Asset,
    AssetGroup,
    Booking,
    CreateNewOrderData,
    Depot,
    DetailedIssueReport,
    DoorStatus,
    IssueReport,
    LocationBookingStats,
    RebookCustomerDoorResult,
    Thing,
    UpdateDepotData,
    UpdateLocationData,
} from './types';
import { currentTenant } from './tenantCacheKey';
import { ProfileInfo } from './types';

const configuredAxios = axios.create();
const newBackendAxios = axios.create({
    baseURL: config.api.newBackendUrl,
});

// The old interim backend is gone (CEO, 2026-09-18): the only slugs that ever
// routed there were the retired v1 spellings `segmenta` / `vonovia`, and
// `useAccessibleTenants` no longer offers them. So there is nothing dynamic
// left to decide — the base URL is set once at creation, like
// `newBackendAxios` above. See DEPRECATED_TENANT_SLUGS in `api/types.ts`.
configuredAxios.defaults.baseURL = config.api.newBaseUrl;

export const sendApiRequest = async (
    request: AxiosRequestConfig,
): Promise<AxiosResponse> => {
    const token = await getToken();
    configuredAxios.defaults.headers.common['Authorization'] =
        'Bearer ' + token;
    let tenant = currentTenant();
    if (tenant === 'vonovia_v2') {
        tenant = 'vonovia';
    }
    if (tenant === 'segmenta_v2') {
        tenant = 'segmenta';
    }
    configuredAxios.defaults.headers.common['X-Tenant'] = tenant;
    return triggerApiRequest(configuredAxios, request);
};

export const sendNewBackendApiRequest = async (
    request: AxiosRequestConfig,
): Promise<AxiosResponse> => {
    const token = await getToken();
    newBackendAxios.defaults.headers.common['Authorization'] =
        'Bearer ' + token;
    let tenant = currentTenant();
    if (tenant === 'vonovia_v2') {
        tenant = 'vonovia';
    }
    if (tenant === 'segmenta_v2') {
        tenant = 'segmenta';
    }
    newBackendAxios.defaults.headers.common['X-Tenant'] = tenant;
    return triggerApiRequest(newBackendAxios, request);
};

const triggerApiRequest = async (
    axiosInstance: AxiosInstance,
    request: AxiosRequestConfig,
): Promise<AxiosResponse> => {
    try {
        return await axiosInstance(request);
    } catch (error) {
        Logger('API').error(request.url);
        throw error;
    }
};

const getBookingDetails = async (bookingId: string): Promise<Booking> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `order/${bookingId}`,
    });

    return processBookingData(response.data);
};

const getMultipleBookingDetails = async (bookingIds: string): Promise<Booking[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `order/multiple/${bookingIds}`,
    });

    return processMultipleBookingsData(response.data);
};

const getVisitorBookings = async (): Promise<Booking[]> => {
    const response = await sendApiRequest({
        url: 'visitor-orders',
    });

    return response.data;
};

const getLocations = async (): Promise<any[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: 'location?includeDisabled=true',
    });

    return response.data.map(processLocationData);
};

const getDepots = async (): Promise<Depot[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: 'depot?includeDisabled=true',
    });

    return response.data.map(processDepotData);
};

const getIssueReports = async (includeArchived = false): Promise<IssueReport[]> => {
    const response = await sendApiRequest({
        url: `damage-reports${includeArchived ? '?includeArchived=true' : ''}`,
    });

    return response.data.map(processIssueReportData);
};

const getIssueReport = async (id: string): Promise<DetailedIssueReport> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `damage-reports/${id}`,
    });

    return response.data;
};

const updateIssueReportStatus = async (id: string, status: string) => {
    const response = await sendApiRequest({
        method: 'post',
        url: `damage-reports/${id}`,
        data: {
            state: status,
        },
    });

    return response.data;
};
const createIssueReports = async (
    message: string,
    assets: {
        assetId?: string;
        depotId: string;
        door: number;
    }[],
    issueType: string,
    service: string,
    bookingNumber: string,
    orderId?: string,
    profileInfo?: ProfileInfo,
    // Location of the reported problem. Sent so a report filed without a
    // locker (issue reports don't always concern one) still keeps its
    // location — otherwise it is only reachable through the asset.
    locationId?: string,
): Promise<{ id: string } | null> => {
    const data = {
        message,
        assets,
        issueType,
        service,
        bookingNumber,
        profileInfo,
        ...(locationId ? { locationId } : {}),
        ...(orderId ? { orderId } : {}),
    };

    const response = await sendApiRequest({
        method: 'post',
        url: 'damage-reports',
        data,
    });

    // The handler answers with the created report; surface its id so callers
    // (e.g. the goodwill-code flow) can link straight to it.
    const created = response?.data?.damageReport;
    const id = created?.id ?? response?.data?.damageReportIds?.[0];
    return id ? { id } : null;
};

const getDepotByID = async (depotId: string): Promise<Depot> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `depot/${depotId}`,
    });

    return response.data;
};

const getDepotAssets = async (depotId: string): Promise<Asset[]> => {
    const response = await sendApiRequest({
        method: 'get',
        // url: `depot/${depotId}/assets`,
        url: 'asset',
    });

    return response.data.map(processAssetData);
};

const getDoorStatus = async (depotId: string): Promise<DoorStatus> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `depot/${depotId}/doors`,
    });

    return { doors: response.data.doorStatus };
};

const openDoor = async (depotId: string, doorNumber: number, actorEmail?: string): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: `depot/${depotId}/doors/${doorNumber}`,
        // Recorded as actor.display on the event log (W2.1) — like
        // finishRental's actorEmail.
        data: actorEmail ? { actorEmail } : undefined,
    });
};

const updateLocation = async (data: UpdateLocationData): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: `location/${data.id}/state`,
        data: {
            enabled: data.enabled,
            status: data.status,
        },
    });
};

const updateDepot = async (data: UpdateDepotData): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: `depot/${data.id}/state`,
        data: {
            enabled: data.enabled,
        },
    });
};

const getThings = async (): Promise<Thing[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: 'things',
    });

    return response.data.map(processThingsData);
};

const getBookingOverview = async (orderId: string): Promise<Thing[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: `order/${orderId}`,
    });

    return response.data;
};

const restartDepot = async (depotId: string): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: `depot/${depotId}/power`,
        data: { mode: 'restart' },
    });
};

const rebookCustomerDoor = async (
    bookingId: string,
    depotId: string,
    itemId: string,
    doorNumber: number,
): Promise<RebookCustomerDoorResult> => {
    const response = await sendApiRequest({
        method: 'post',
        url: `orders/${bookingId}/block/${depotId}/orderItem/${itemId}/door/${doorNumber}`,
    });

    return {
        oldDoor: Number(response.data.oldDoor),
        newDoor: Number(response.data.newDoor),
    };
};

const finishRental = async (
    orderId: string,
    items: string[],
): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: 'rental/finish',
        data: {
            orderId: orderId,
            orderItems: items,
        },
    });
};

const startRental = async (orderId: string, items: string[]): Promise<void> => {
    await sendApiRequest({
        method: 'post',
        url: 'rental/start',
        data: {
            orderId: orderId,
            orderItems: items,
        },
    });
};

const getLocationBookingStats = async (
    locationId: number,
    fromDate: Date,
): Promise<LocationBookingStats> => {
    const from = moment(fromDate).format('YYYY-MM-DD');

    const response = await sendApiRequest({
        method: 'get',
        url: `location/${locationId}/stats?from=${from}`,
    });

    return processLocationBookingStats(response.data);
};

const fetchAllLocationAssetGroups = async (): Promise<AssetGroup[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: 'group',
    });

    return processAssetGroupsData(response.data);
};

const fetchAssetsForIssueReport = async (): Promise<ApartmentAssets[]> => {
    const response = await sendApiRequest({
        method: 'get',
        url: 'asset/for-issue-report',
    });

    return response.data;
};

const fetchLocationAssetGroupAvailabilities = async (
    assetGroupId: string,
    from: Date,
    to: Date,
): Promise<AssetGroup> => {
    const fromDate = from.toISOString();
    const toDate = to.toISOString();
    const response = await sendApiRequest({
        method: 'get',
        url: `group/${assetGroupId}/availabilities/${fromDate}/${toDate}`,
    });

    return processAssetGroupData(response.data);
};

export const createNewOrder = async (
    data: CreateNewOrderData,
): Promise<void> => {
    const response = await sendApiRequest({
        method: 'post',
        url: 'order',
        data: data,
    });
    return response.data;
};

export const Api = {
    getBookingDetails,
    getMultipleBookingDetails,
    getVisitorBookings,
    getLocations,
    getDepots,
    getDepotByID,
    getDepotAssets,
    getIssueReports,
    getIssueReport,
    getDoorStatus,
    openDoor,
    updateLocation,
    updateDepot,
    getThings,
    restartDepot,
    rebookCustomerDoor,
    finishRental,
    startRental,
    getLocationBookingStats,
    createIssueReports,
    updateIssueReportStatus,
    getBookingOverview,
    fetchAllLocationAssetGroups,
    fetchLocationAssetGroupAvailabilities,
    createNewOrder,
    fetchAssetsForIssueReport,
};
